// =============================================================================
// Origin UI — src/pages/approvals/LieuApprovalReviewPage.tsx
// Day-in-lieu claim review (Oct 2026) — opened from the Approvals Inbox,
// the home screen and the approver email.
// Line manager / HR approve or reject (reason mandatory to reject).
// HR Admin can revoke an approved claim (reason mandatory).
// =============================================================================

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '@/services/apiService';
import { lieuApi, formatLieuDate, formatLieuDays, type LieuClaim } from '@/services/lieuApi';
import type { LeaveBalance } from '@/services/leaveApi';
import { PageHeader, Card, ErrorBanner, LoadingSpinner } from '@/components/common';
import { LieuStatusPill, LieuApprovalProgress, LieuTimeline } from '@/pages/leave/LieuComponents';
import styles from '@/pages/leave/lieu.module.css';

export function LieuApprovalReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [claim, setClaim] = useState<LieuClaim | null>(null);
  const [toil, setToil] = useState<LeaveBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comments, setComments] = useState('');
  const [revokeReason, setRevokeReason] = useState('');
  const [acting, setActing] = useState(false);
  const [done, setDone] = useState('');

  useEffect(() => { if (id) load(); }, [id]);

  async function load() {
    try {
      setLoading(true);
      setError('');
      const c = await lieuApi.get(id!);
      setClaim(c);
      try {
        const bal = await api.get<LeaveBalance[]>(`/api/leave/team/balance/${c.employeeId}`);
        setToil(bal.find(b => b.leaveTypeCode === 'TOIL') || null);
      } catch { /* balance is context only */ }
    } catch (err: any) {
      setError(err.message || 'Failed to load the claim');
    } finally {
      setLoading(false);
    }
  }

  async function act(kind: 'approve' | 'reject' | 'revoke') {
    if (!claim) return;
    if (kind === 'reject' && !comments.trim()) { setError('Please give a reason for rejecting — the employee will see it.'); return; }
    if (kind === 'revoke' && !revokeReason.trim()) { setError('Please give a reason for revoking.'); return; }
    try {
      setActing(true);
      setError('');
      const updated = kind === 'approve'
        ? await lieuApi.approve(claim.lieuDayClaimId, comments.trim() || undefined)
        : kind === 'reject'
          ? await lieuApi.reject(claim.lieuDayClaimId, comments.trim())
          : await lieuApi.revoke(claim.lieuDayClaimId, revokeReason.trim());
      setClaim(updated);
      setComments('');
      setRevokeReason('');
      setDone(
        kind === 'approve'
          ? updated.status === 'Approved'
            ? 'Approved. The day has been credited to their TOIL balance and they have been notified.'
            : 'Your approval is recorded. The claim is now waiting for the other approver.'
          : kind === 'reject'
            ? 'Rejected. The employee has been emailed your reason and can add more information and resubmit.'
            : 'Revoked. The TOIL credit has been removed and the employee notified.'
      );
      if (kind !== 'revoke') setTimeout(() => navigate('/approvals'), 2200);
    } catch (err: any) {
      setError(err.message || 'Action failed');
    } finally {
      setActing(false);
    }
  }

  if (loading) return <LoadingSpinner message="Loading claim..." />;
  if (!claim) return <ErrorBanner message={error || 'Claim not found'} />;

  return (
    <div className={styles.page}>
      <div>
        <Link to="/approvals" className={styles.backLink}>← Back to Approvals</Link>
      </div>
      <PageHeader title="Day in Lieu Claim" subtitle={`${claim.employeeName} — worked ${formatLieuDate(claim.workedDate)}`} />

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {done && <div className={styles.successBanner}>{done}</div>}

      <div className={styles.reviewLayout}>
        <div className={styles.reviewMain}>
          <Card>
            <h3 className={styles.sectionTitle}>Claim</h3>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Employee</span>
                <span className={styles.detailValue}>{claim.employeeName} ({claim.employeeNumber})</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Department · Office</span>
                <span className={styles.detailValue}>{claim.department || '—'} · {claim.officeLocation || '—'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Date worked</span>
                <span className={styles.detailValue}>{formatLieuDate(claim.workedDate)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Amount</span>
                <span className={styles.detailValue} style={{ fontWeight: 700 }}>{formatLieuDays(claim.days)}</span>
              </div>
              <div className={`${styles.detailItem} ${styles.detailItemFull}`}>
                <span className={styles.detailLabel}>Work done</span>
                <span className={styles.detailValue}>{claim.reason}</span>
              </div>
              {toil && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Current TOIL available</span>
                  <span className={styles.detailValue}>{toil.available} day{toil.available === 1 ? '' : 's'}</span>
                </div>
              )}
              {claim.resubmissionCount > 0 && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Resubmitted</span>
                  <span className={styles.detailValue}>{claim.resubmissionCount} time{claim.resubmissionCount === 1 ? '' : 's'}</span>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <h3 className={styles.sectionTitle}>Approvals</h3>
            <LieuApprovalProgress claim={claim} />
          </Card>

          <Card>
            <h3 className={styles.sectionTitle}>History</h3>
            <LieuTimeline claim={claim} />
          </Card>
        </div>

        <div className={styles.reviewSide}>
          <div className={styles.panel}>
            <div className={styles.panelTitle}>Status</div>
            <div><LieuStatusPill claim={claim} /></div>
            <div className={styles.hint}>Submitted {formatLieuDate(claim.submittedAt, false)}</div>
          </div>

          {claim.canApprove && (
            <div className={styles.panel}>
              <div className={styles.panelTitle}>Your decision</div>
              <div className={styles.hint}>
                You are approving as <strong>{claim.myApprovalRole}</strong>.
                {claim.myApprovalRole === 'Line manager & HR' && ' Your single approval covers both steps.'}
              </div>
              <textarea
                className={styles.textarea}
                rows={3}
                value={comments}
                onChange={e => setComments(e.target.value)}
                placeholder="Comment (optional to approve, required to reject)"
                aria-label="Decision comment"
              />
              <button className={styles.approveBtn} onClick={() => act('approve')} disabled={acting}>
                {acting ? 'Working…' : 'Approve'}
              </button>
              <button className={styles.rejectBtn} onClick={() => act('reject')} disabled={acting || !comments.trim()}
                title={!comments.trim() ? 'Enter a reason to reject' : undefined}>
                Reject
              </button>
            </div>
          )}

          {claim.canRevoke && (
            <div className={styles.panel}>
              <div className={styles.panelTitle}>HR actions</div>
              <div className={styles.hint}>Revoking removes the TOIL credit. If the days have already been used, the TOIL balance will go negative.</div>
              <textarea
                className={styles.textarea}
                rows={2}
                value={revokeReason}
                onChange={e => setRevokeReason(e.target.value)}
                placeholder="Reason for revoking (required)"
                aria-label="Revoke reason"
              />
              <button className={styles.rejectBtn} onClick={() => act('revoke')} disabled={acting || !revokeReason.trim()}>
                Revoke Claim
              </button>
            </div>
          )}

          {!claim.canApprove && !claim.canRevoke && claim.status === 'Pending' && (
            <div className={styles.infoNote}>
              Nothing for you to do here — this claim is {claim.statusLabel.toLowerCase()}.
            </div>
          )}

          {claim.decisionComments && claim.status !== 'Pending' && (
            <div className={claim.status === 'Approved' ? styles.infoNote : styles.rejectionNote}>
              <strong>{claim.status === 'Approved' ? 'Comment' : 'Reason'}:</strong> {claim.decisionComments}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
